import React from "react";
import { Helmet } from "react-helmet-async";
import { Container, Typography, Box, Paper } from "@mui/material";
import Banner from "../components/Banner";

const About: React.FC = () => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        background: `
        url('/event-background.webp'),
        radial-gradient(circle at top left, #041C2C 0%, #22143C 100%)
      `,
        backgroundBlendMode: "soft-light, normal",
        backgroundSize: "cover, cover, cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        color: "#E3D9D6",
      }}
    >
      <>
        {/* Meta tags for SEO */}
        <Helmet>
          <title>About | Event Spy</title>
          <meta
            name="description"
            content="We're on a mission to help fans stop overpaying for event tickets. Learn more about Event Spy, the app that tracks ticket prices so you never miss a deal"
          />
        </Helmet>
      </>
      <Banner />
      <Container maxWidth="lg" sx={{ py: 15 }}>
        <Paper
          sx={{
            p: 6,
            background: "rgba(17, 25, 44, 0.7)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 0 20px rgba(0, 0, 0, 0.3)",
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              color: "#E3D9D6",
              textAlign: "center",
              mb: 6,
              fontWeight: 700,
            }}
          >
            About
          </Typography>

          <Typography
            variant="body1"
            component="h2"
            sx={{ color: "#E3D9D6", mb: 3, fontSize: "1.5rem" }}
          >
            Welcome to Event Spy — the smartest way to stay ahead of ticket
            prices across major resale platforms.
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: "#E3D9D6", mb: 4, fontSize: "1.5rem" }}
          >
            We help fans save money on tickets by tracking resale ticket prices
            from top platforms like <strong>Ticketmaster</strong>,{" "}
            <strong>TickPick</strong>, <strong>VividSeats</strong>,{" "}
            <strong>SeatGeek</strong> and <strong>StubHub</strong>. Our service
            scans these marketplaces regularly and alerts you when prices for
            your favorite events drop below your target.
          </Typography>

          <Typography
            variant="h4"
            component="h2"
            sx={{ color: "#E3D9D6", mb: 3, mt: 6, fontWeight: 700 }}
          >
            Why Use Us?
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#E3D9D6", mb: 2, fontSize: "1.5rem" }}
          >
            Most ticket buyers overpay without realizing there was a better
            price yesterday — or even an hour ago. We fix that.
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#E3D9D6", fontSize: "1.5rem" }}
          >
            With smart ticket price tracking, personalized alerts, and event
            analytics, we make sure you buy at the right time, not just the
            right place.
          </Typography>

          <Typography
            variant="h4"
            component="h2"
            sx={{ color: "#E3D9D6", mb: 3, mt: 6, fontWeight: 700 }}
          >
            What We Do
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#E3D9D6", mb: 4, fontSize: "1.5rem" }}
          >
            Our comprehensive service tracks event ticket prices across the
            biggest resale sites, carefully matching tickets against your
            personal price and quantity criteria. When deals are found, we
            instantly notify you through email or WhatsApp alerts.
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#E3D9D6", mb: 4, fontSize: "1.5rem" }}
          >
            Beyond just notifications, we provide detailed analytics that let
            you track trends, monitor price drops, and understand market
            performance over time, giving you the complete picture of ticket
            pricing and availability.
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: "#E3D9D6", mb: 4, fontSize: "1.5rem" }}
          >
            Whether you're tracking tickets for a concert, sports game, or
            theater event, our platform makes sure you're the first to know when
            the best deals appear.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default About;
